// ===================================
// ACHIEVEMENTS PAGE
// src/pages/Achievements.js
// Stage 11: Gamification UI with badges,
// streak calendar, goals, leaderboard
// ===================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import './Achievements.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const Achievements = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [userData, setUserData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [goals, setGoals] = useState([]);
  const [newAchievements, setNewAchievements] = useState([]);

  const fetchUserAchievements = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/achievements/user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch achievements');

      const data = await response.json();
      setUserData(data);
    } catch (err) {
      console.error('Error fetching achievements:', err);
    }
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/achievements/leaderboard?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch leaderboard');

      const data = await response.json();
      setLeaderboard(data.leaderboard || []);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    }
  }, []);

  const loadGoals = useCallback(() => {
    const savedGoals = localStorage.getItem('userGoals');
    if (savedGoals) {
      setGoals(JSON.parse(savedGoals));
    } else {
      setGoals([
        { id: 1, title: 'Complete 5 interviews', target: 5, current: 0, type: 'sessions' },
        { id: 2, title: 'Reach 80% average score', target: 80, current: 0, type: 'score' },
        { id: 3, title: 'Maintain 7-day streak', target: 7, current: 0, type: 'streak' }
      ]);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUserAchievements(), fetchLeaderboard()]);
      loadGoals();
      setLoading(false);
    };
    loadData();
  }, [fetchUserAchievements, fetchLeaderboard, loadGoals]);

  useEffect(() => {
    if (userData) {
      setGoals(prev => prev.map(goal => {
        switch(goal.type) {
          case 'sessions':
            return { ...goal, current: userData.earned_achievements?.length || 0 };
          case 'streak':
            return { ...goal, current: userData.points?.streak?.current || 0 };
          default:
            return goal;
        }
      }));
    }
  }, [userData]);

  const getBadgeColor = (color) => {
    const colors = {
      gold: 'linear-gradient(135deg, #fbbf24, #d97706)',
      silver: 'linear-gradient(135deg, #9ca3af, #6b7280)',
      bronze: 'linear-gradient(135deg, #d97706, #92400e)',
      purple: 'linear-gradient(135deg, #a855f7, #7c3aed)',
      diamond: 'linear-gradient(135deg, #60a5fa, #3b82f6)',
      green: 'linear-gradient(135deg, #22c55e, #16a34a)',
      orange: 'linear-gradient(135deg, #f97316, #ea580c)',
      red: 'linear-gradient(135deg, #ef4444, #dc2626)',
      teal: 'linear-gradient(135deg, #14b8a6, #0d9488)'
    };
    return colors[color] || colors.gold;
  };

  const generateStreakCalendar = () => {
    const today = new Date();
    const days = [];
    const currentStreak = userData?.points?.streak?.current || 0;

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const isActive = i < currentStreak;
      const isToday = i === 0;
      days.push({ date, day: date.getDate(), isActive, isToday });
    }
    return days;
  };

  const getLevelProgress = () => {
    if (!userData?.points?.level) return 0;
    const currentLevelPoints = userData.levels?.find(l => l.level === userData.points.level.current)?.min_points || 0;
    const nextLevelPoints = userData.levels?.find(l => l.level === userData.points.level.current + 1)?.min_points || currentLevelPoints + 100;
    const total = nextLevelPoints - currentLevelPoints;
    const progress = userData.points.lifetime - currentLevelPoints;
    return Math.min(100, Math.max(0, (progress / total) * 100));
  };

  if (loading) {
    return (
      <div className="achievements-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading achievements...</p>
        </div>
      </div>
    );
  }

  const points = userData?.points || {};
  const earnedAchievements = userData?.earned_achievements || [];
  const allAchievements = userData?.all_achievements || [];
  const streakCalendar = generateStreakCalendar();

  return (
    <div className="achievements-page">
      <div className="achievements-container">
        <div className="page-header">
          <div>
            <h1>🏆 Achievements</h1>
            <p>Track your progress, earn badges, and compete!</p>
          </div>
        </div>

        {/* Level Banner */}
        <div className="level-banner">
          <div className="level-info">
            <div className="level-badge">
              <span className="level-number">{points.level?.current || 1}</span>
            </div>
            <div className="level-details">
              <h2>{points.level?.title || 'Beginner'}</h2>
              <p>{points.lifetime || 0} lifetime points</p>
            </div>
          </div>
          <div className="level-progress-section">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${getLevelProgress()}%` }}></div>
            </div>
            {points.level?.next && (
              <p className="progress-text">{points.level.points_to_next} points to {points.level.next}</p>
            )}
          </div>
          <div className="current-points">
            <span className="points-value">{points.current || 0}</span>
            <span className="points-label">Available Points</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="achievements-tabs">
          {['overview', 'badges', 'goals', 'leaderboard'].map(tab => (
            <button key={tab} className={`tab-btn ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
              {tab === 'overview' && '📊'}{tab === 'badges' && '🏅'}{tab === 'goals' && '🎯'}{tab === 'leaderboard' && '👑'}
              <span>{tab.charAt(0).toUpperCase() + tab.slice(1)}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="stats-row">
                <div className="stat-card"><div className="stat-icon">🔥</div><div className="stat-value">{points.streak?.current || 0}</div><div className="stat-label">Day Streak</div></div>
                <div className="stat-card"><div className="stat-icon">📈</div><div className="stat-value">{points.streak?.longest || 0}</div><div className="stat-label">Best Streak</div></div>
                <div className="stat-card"><div className="stat-icon">🏅</div><div className="stat-value">{earnedAchievements.length}</div><div className="stat-label">Badges Earned</div></div>
                <div className="stat-card"><div className="stat-icon">🎯</div><div className="stat-value">{allAchievements.length}</div><div className="stat-label">Total Available</div></div>
              </div>

              <div className="streak-calendar-card">
                <h3>🔥 Activity Streak</h3>
                <div className="streak-calendar">
                  {streakCalendar.map((day, i) => (
                    <div key={i} className={`calendar-day ${day.isActive ? 'active' : ''} ${day.isToday ? 'today' : ''}`} title={day.date.toLocaleDateString()}>
                      <span className="day-number">{day.day}</span>
                    </div>
                  ))}
                </div>
                <div className="streak-legend">
                  <span><span className="dot inactive"></span> No activity</span>
                  <span><span className="dot active"></span> Practice day</span>
                </div>
              </div>

              <div className="recent-badges-card">
                <h3>🏅 Recent Badges</h3>
                {earnedAchievements.length > 0 ? (
                  <div className="badges-row">
                    {earnedAchievements.slice(0, 5).map(badge => (
                      <div key={badge.id} className="badge-item earned">
                        <div className="badge-icon" style={{ background: getBadgeColor(badge.badge_color) }}>{badge.icon}</div>
                        <span className="badge-name">{badge.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="empty-message">Complete sessions to earn your first badge!</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'badges' && (
            <div className="badges-tab">
              <div className="badges-grid">
                {allAchievements.map(achievement => {
                  const isEarned = achievement.earned;
                  return (
                    <div key={achievement.id} className={`badge-card ${isEarned ? 'earned' : 'locked'}`}>
                      <div className="badge-icon-large" style={{ background: isEarned ? getBadgeColor(achievement.badge_color) : 'rgba(255,255,255,0.1)' }}>
                        {isEarned ? achievement.icon : '🔒'}
                      </div>
                      <div className="badge-info">
                        <h4>{achievement.name}</h4>
                        <p>{achievement.description}</p>
                        <span className="badge-points">+{achievement.points} pts</span>
                      </div>
                      {isEarned && <div className="earned-badge">✓ Earned</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'goals' && (
            <div className="goals-tab">
              <div className="goals-header"><h3>Your Goals</h3></div>
              <div className="goals-list">
                {goals.map(goal => {
                  const progress = Math.min(100, (goal.current / goal.target) * 100);
                  const isComplete = progress >= 100;
                  return (
                    <div key={goal.id} className={`goal-card ${isComplete ? 'complete' : ''}`}>
                      <div className="goal-icon">{isComplete ? '✅' : '🎯'}</div>
                      <div className="goal-content">
                        <h4>{goal.title}</h4>
                        <div className="goal-progress-bar"><div className="goal-progress-fill" style={{ width: `${progress}%` }}></div></div>
                        <span className="goal-progress-text">{goal.current} / {goal.target}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="suggested-goals">
                <h4>Suggested Goals</h4>
                <div className="suggested-list">
                  <button className="suggested-goal"><span>🏃</span> Complete 10 interviews this month</button>
                  <button className="suggested-goal"><span>⭐</span> Score 90%+ on 3 interviews</button>
                  <button className="suggested-goal"><span>🔥</span> Maintain a 14-day streak</button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'leaderboard' && (
            <div className="leaderboard-tab">
              <div className="leaderboard-list">
                {leaderboard.map((player, index) => {
                  const isCurrentUser = player.id === user?.id;
                  return (
                    <div key={player.id} className={`leaderboard-item ${isCurrentUser ? 'current-user' : ''}`}>
                      <div className="rank">{index === 0 && '🥇'}{index === 1 && '🥈'}{index === 2 && '🥉'}{index > 2 && `#${index + 1}`}</div>
                      <div className="player-info">
                        <div className="player-avatar">{player.name?.charAt(0) || player.username?.charAt(0) || '?'}</div>
                        <div className="player-details">
                          <span className="player-name">{player.name || player.username}{isCurrentUser && <span className="you-badge">You</span>}</span>
                          <span className="player-level">{player.level_title}</span>
                        </div>
                      </div>
                      <div className="player-stats">
                        <span className="player-points">{player.lifetime_points} pts</span>
                        <span className="player-badges">🏅 {player.achievement_count}</span>
                      </div>
                    </div>
                  );
                })}
                {leaderboard.length === 0 && <div className="empty-leaderboard"><p>No leaderboard data yet.</p></div>}
              </div>
            </div>
          )}
        </div>

        {newAchievements.length > 0 && (
          <div className="achievement-notification">
            <div className="notification-content">
              <span className="notification-icon">🎉</span>
              <div><h4>New Achievement Unlocked!</h4><p>{newAchievements[0].name}</p></div>
            </div>
            <button onClick={() => setNewAchievements([])}>×</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Achievements;
